/**
 * SOLID Review
 * S — Strong. One concern: how many seconds the reconnecting overlay has shown.
 * O — Good. Another counter is another field.
 * L — Strong. A view model (`IServiceViewModel`).
 * I — Strong. The seconds, the view's one input.
 * D — Strong. The timers.
 */
import type { IServiceViewModel } from './IServiceViewModel';
import { ObservableState } from './ObservableState';
import { Reactions } from './Reactions';
import type { IServiceViewTimers } from './platform/IConfigDepViewPlatform';

export interface IStateReconnecting {
  readonly elapsedSeconds: number;
}

/** What the overlay's view hands it: whether it shows. */
type TOverlayInputs = readonly [visible: boolean];

const ZERO: IStateReconnecting = { elapsedSeconds: 0 };
const RESET: Partial<IStateReconnecting> = { elapsedSeconds: 0 };

/**
 * THE RECONNECTING OVERLAY'S CLOCK (frontend-framework-oo P4, 2026-10-29, §28.296) — `components/game/ReconnectingOverlay.tsx`'s state
 * and effect: from zero each time it shows, a second at a time; back to zero when it hides.
 */
export class ReconnectingOverlayViewModel implements IServiceViewModel<IStateReconnecting> {
  private readonly state = new ObservableState<IStateReconnecting>(ZERO);
  private readonly reactions = new Reactions<TOverlayInputs>([{
    deps: (inputs) => inputs,
    run: ([visible]) => {
      this.state.update(RESET);
      if (!visible) return undefined;
      const interval = this.timers.setInterval(() => {
        const ticked: Partial<IStateReconnecting> = { elapsedSeconds: this.state.get().elapsedSeconds + 1 };
        this.state.update(ticked);
      }, 1000);
      return () => this.timers.clearInterval(interval);
    },
  }]);

  constructor(private readonly timers: IServiceViewTimers) {}

  getSnapshot(): IStateReconnecting {
    return this.state.get();
  }

  subscribe(listener: () => void): () => void {
    return this.state.subscribe(listener);
  }

  attach(): void {}

  detach(): void {
    this.reactions.stop();
  }

  release(inputs: readonly unknown[]): void {
    this.reactions.stopChanged(inputs as TOverlayInputs);
  }

  commit(inputs: readonly unknown[]): void {
    this.reactions.startChanged(inputs as TOverlayInputs);
  }
}
